import {defineStore} from 'pinia'
import type {Role, Permission, RolePermission} from '@/types/Role'
import Srv from '@/services/GraphQLSrv'
import type {AnyObject} from '@/types/Common'
import {attributesToObject, objectToAttributes, getBindingData} from '@/helpers/graphql'

// Три основных категории permissions (папки с lazy-load)
const CATEGORY_NAMES = ['view', 'gql', 'middleware']

export const useRolesStore = defineStore('roles', {
  state: () => ({
    roles: [] as Role[],
    permissions: [] as Permission[],
    rolePermissions: [] as RolePermission[],
    rolesLoading: true,
    permissionsLoading: false,
    rolesPagination: {
      currentPage: 1,
      perPage: 20,
      total: 0,
      hasMorePages: false,
      lastPage: 1,
    },
  }),
  getters: {
    getRoleByUuid: state => (uuid: string) => {
      return state.roles.find(role => role.uuid === uuid) || null
    },
    // Построить дерево permissions из плоского списка
    permissionsTree: (state): Permission[] => {
      const permissionsMap = new Map(state.permissions.map(p => [p.uuid, {...p, children: []}]))
      const roots: Permission[] = []

      for (const permission of state.permissions) {
        if (permission.parentUuid) {
          const parent = permissionsMap.get(permission.parentUuid)
          if (parent) {
            if (!parent.children) parent.children = []
            parent.children.push(permissionsMap.get(permission.uuid)!)
          }
        } else {
          roots.push(permissionsMap.get(permission.uuid)!)
        }
      }

      return roots
    },
    // Получить permissions по категории
    permissionsByCategory: (state) => (category: string): Permission[] => {
      return state.permissions.filter(p => p.category === category)
    },
  },
  actions: {
    async reqRoles(page = 1, perPage = 20) {
      this.rolesLoading = true

      try {
        const res = await Srv.query(
          'roles',
          [
            'uuid',
            'name',
            'attributes',
          ],
          true,
          {page, perPage}
        )

        // Преобразуем attributes в удобный формат
        this.roles = (res.list || []).map((role: any) => {
          const attrs = attributesToObject(role.attributes || [])
          return {
            uuid: role.uuid,
            name: role.name,
            titleRu: attrs.titleRu || role.name,  // Если нет titleRu в attributes, используем name
            titleKz: attrs.titleKz || role.name,  // Если нет titleKz в attributes, используем name
            permissions: [],  // Будет загружаться отдельно через fetchRolePermissions
            endpoints: attrs.endpoints || [],
          }
        })

        const {data, ...pagination} = res.data
        this.rolesPagination = {...pagination}
      } catch (error) {
        this.roles = []
      }

      this.rolesLoading = false
    },
    // Гидрация из батч-запроса (принимает сырые данные без HTTP запроса)
    hydrateRoles(rawList: any[]) {
      this.rolesLoading = true
      try {
        this.roles = (rawList || []).map((role: any) => {
          const attrs = attributesToObject(role.attributes || [])
          return {
            uuid: role.uuid,
            name: role.name,
            titleRu: attrs.titleRu || role.name,
            titleKz: attrs.titleKz || role.name,
            permissions: [],
            endpoints: attrs.endpoints || [],
          }
        })
      } catch (error) {
        this.roles = []
      }
      this.rolesLoading = false
    },
    async reqPermissionsUuid(roleUuid: string, permissions: string[]) {
      const res = await Srv.query('PermissionsQuery', ['uuid', 'name'], true)

      const permissionsMap = new Map<string, string>(res.list.map(p => [p.name, p.uuid]))

      return await Promise.all((permissions || []).map(name => {
        if (permissionsMap.has(name)) {
          return permissionsMap.get(name)
        }

        return Srv.mutation('permissionCreate', ['uuid', 'name'], {name, roleUuids: [roleUuid]})
          .then(data => data.data.uuid)
      }).filter(Boolean))
    },
    async createOrUpdateRole(type: 'create' | 'update', {uuid: id, name}: AnyObject) {
      if (type === 'create') {
        const {data: role} = await Srv.mutation('roleCreate', ['uuid', 'name'], {name})
        return role  // Возвращаем созданную роль с uuid
      } else {
        await Srv.mutation('roleUpdate', ['uuid', 'name'], {
          uuid: id,
          name,
        })
        return null
      }
    },

    // Вспомогательный метод для преобразования raw permission в Permission
    _parsePermission(perm: any, category?: string, inheritedCatalogUuid?: string | null): Permission {
      const attrs = attributesToObject(perm.attributes || [])
      return {
        uuid: perm.uuid,
        name: perm.name,
        parentUuid: perm.parentUuid,
        titleRu: attrs.titleRu || null,
        titleKz: attrs.titleKz || null,
        catalogUuid: attrs.catalogUuid || inheritedCatalogUuid || null,
        category: (category || undefined) as any,
      }
    },

    // Загрузка корневых permissions (parentUuid is null)
    // Категории (view, gql, middleware) — папки с lazy-load
    // Остальные — обычные leaf permissions
    async fetchPermissions() {
      this.permissionsLoading = true
      try {
        // Загружаем все root permissions с пагинацией
        const allRoots: Permission[] = []
        let page = 1
        let hasMore = true

        while (hasMore) {
          const res = await Srv.query(
            'PermissionsQuery',
            ['uuid', 'name', 'parentUuid', 'attributes'],
            true,
            {page, perPage: 500, parentUuidIsNull: true}
          )

          const batch = (res.list || []).map((perm: any) => {
            const isCategory = CATEGORY_NAMES.includes(perm.name)
            const p = this._parsePermission(perm, isCategory ? perm.name : undefined)
            return p
          })

          allRoots.push(...batch)
          hasMore = res.data?.hasMorePages ?? false
          page++
        }

        this.permissions = allRoots
        return this.permissions
      } finally {
        this.permissionsLoading = false
      }
    },

    // NEDB-1774: server-side поиск permissions по technical name.
    // Использует `filters: {name: {operator: "like", value}}` — бэк превращает в
    // case-insensitive $regex. Загруженное сохраняется в общий массив permissions
    // (без дубликатов), чтобы потом можно было переиспользовать в дереве.
    async searchPermissionsByName(query: string, perPage = 300): Promise<Permission[]> {
      const res = await Srv.query(
        'PermissionsQuery',
        ['uuid', 'name', 'parentUuid', 'attributes'],
        true,
        {
          filters: {name: {operator: 'like', value: query}},
          page: 1,
          perPage,
        },
      )
      const items = (res.list || []).map((p: any) => {
        const isCategory = !p.parentUuid && CATEGORY_NAMES.includes(p.name)
        return this._parsePermission(p, isCategory ? p.name : undefined)
      })
      const existingUuids = new Set(this.permissions.map(p => p.uuid))
      const newOnes = items.filter(p => !existingUuids.has(p.uuid))
      if (newOnes.length) this.permissions = [...this.permissions, ...newOnes]
      return items
    },

    // NEDB-1774: догрузка предков по цепочке parentUuid до корня.
    // Нужно чтобы для найденных через search permissions знать категорию/путь.
    // Итеративная схема: батчами по uuids, пока не упрёмся в root.
    async ensureAncestorsLoaded(uuids: string[]): Promise<void> {
      const loaded = new Set(this.permissions.map(p => p.uuid))
      let toFetch = uuids.filter(u => u && !loaded.has(u))
      let safetyGuard = 10
      while (toFetch.length && safetyGuard-- > 0) {
        const res = await Srv.query(
          'PermissionsQuery',
          ['uuid', 'name', 'parentUuid', 'attributes'],
          true,
          {uuids: toFetch, page: 1, perPage: toFetch.length},
        )
        const items = (res.list || []).map((p: any) => {
          const isCategory = !p.parentUuid && CATEGORY_NAMES.includes(p.name)
          return this._parsePermission(p, isCategory ? p.name : undefined)
        })
        const newOnes = items.filter(p => !loaded.has(p.uuid))
        if (newOnes.length) this.permissions = [...this.permissions, ...newOnes]
        for (const p of items) loaded.add(p.uuid)
        toFetch = items.map(p => p.parentUuid).filter((u): u is string => !!u && !loaded.has(u))
      }
    },

    // Загрузка дочерних permissions по parentUuid (с пагинацией через все страницы)
    async fetchChildPermissions(parentUuid: string): Promise<Permission[]> {
      const allChildren: Permission[] = []
      let page = 1
      let hasMore = true

      // Определяем категорию и catalogUuid от родителя
      const parent = this.permissions.find(p => p.uuid === parentUuid)
      const category = parent?.category
      const catalogUuid = parent?.catalogUuid

      while (hasMore) {
        const res = await Srv.query(
          'PermissionsQuery',
          ['uuid', 'name', 'parentUuid', 'attributes'],
          true,
          {page, perPage: 500, parentUuid}
        )

        const children = (res.list || []).map((perm: any) => {
          return this._parsePermission(perm, category as string, catalogUuid)
        })

        allChildren.push(...children)

        hasMore = res.data?.hasMorePages ?? false
        page++
      }

      // Добавляем загруженных детей в общий массив permissions (без дубликатов)
      const existingUuids = new Set(this.permissions.map(p => p.uuid))
      const newPermissions = allChildren.filter(p => !existingUuids.has(p.uuid))
      this.permissions = [...this.permissions, ...newPermissions]

      return allChildren
    },

    // Создание нового permission. Возвращает Permission и добавляет его в state.permissions
    // (без дубликатов), чтобы дерево/поиск обновились без re-fetch. Категория/catalogUuid
    // наследуются от родителя — так же, как это делает fetchChildPermissions.
    async createPermission(params: {
      name: string
      parentUuid: string
      titleRu?: string
      titleKz?: string
    }): Promise<Permission> {
      const attrsObj: Record<string, any> = {}
      if (params.titleRu) attrsObj.titleRu = params.titleRu
      if (params.titleKz) attrsObj.titleKz = params.titleKz

      const res = await Srv.mutation(
        'permissionCreate',
        ['uuid', 'name', 'parentUuid', 'attributes'],
        {
          name: params.name,
          parentUuid: params.parentUuid,
          attributes: objectToAttributes(attrsObj),
        },
      )
      if (!res.data) {
        throw new Error(res.message || 'permissionCreate returned no data')
      }

      const parent = this.permissions.find(p => p.uuid === params.parentUuid)
      const created = this._parsePermission(
        res.data,
        parent?.category as string | undefined,
        parent?.catalogUuid ?? null,
      )

      if (!this.permissions.some(p => p.uuid === created.uuid)) {
        this.permissions = [...this.permissions, created]
      }

      return created
    },

    // Перенос orphan-а в категорию (view/gql/middleware). Меняем parentUuid через
    // permissionUpdate и локально обновляем category/catalogUuid у самого узла и
    // всех его потомков — в БД эти поля наследуются на лету по цепочке parent, но
    // в store хранятся как поля объекта.
    //
    // Перед мутацией догружаем детей целевой папки и проверяем name-коллизию — если
    // permission с таким именем уже там есть, отменяем перенос и бросаем NAME_COLLISION,
    // чтобы UI показал понятное сообщение. Иначе получим двух «foo» в одной папке.
    async movePermission(uuid: string, newParentUuid: string): Promise<Permission> {
      const src = this.permissions.find(p => p.uuid === uuid)
      if (!src) throw new Error('movePermission: permission not found in store')

      await this.fetchChildPermissions(newParentUuid)
      const clash = this.permissions.find(
        p => p.parentUuid === newParentUuid && p.name === src.name && p.uuid !== uuid,
      )
      if (clash) {
        const e = new Error('NAME_COLLISION') as Error & {code?: string}
        e.code = 'NAME_COLLISION'
        throw e
      }

      // Шлём name вместе с parentUuid — некоторые бэки при partial update
      // сохраняют только полный набор полей, без name мутация может быть no-op.
      const res = await Srv.mutation(
        'permissionUpdate',
        ['uuid', 'name', 'parentUuid', 'attributes'],
        {uuid, name: src.name, parentUuid: newParentUuid},
      )
      if (!res.data) {
        throw new Error(res.message || 'permissionUpdate returned no data')
      }

      const newParent = this.permissions.find(p => p.uuid === newParentUuid)
      const newCategory = newParent?.category
      const newCatalogUuid = newParent?.catalogUuid ?? null

      const childrenByParent = new Map<string, Permission[]>()
      for (const p of this.permissions) {
        if (p.parentUuid) {
          const arr = childrenByParent.get(p.parentUuid) ?? []
          arr.push(p)
          childrenByParent.set(p.parentUuid, arr)
        }
      }

      const updates = new Map<string, Permission>()
      const rootIdx = this.permissions.findIndex(p => p.uuid === uuid)
      if (rootIdx === -1) throw new Error('movePermission: permission not found in store')
      updates.set(uuid, {
        ...this.permissions[rootIdx],
        parentUuid: newParentUuid,
        category: newCategory as any,
        catalogUuid: newCatalogUuid,
      })

      const stack: string[] = [uuid]
      while (stack.length) {
        const parentId = stack.pop()!
        const parentUpd = updates.get(parentId)!
        for (const child of childrenByParent.get(parentId) ?? []) {
          updates.set(child.uuid, {
            ...child,
            category: parentUpd.category,
            catalogUuid: parentUpd.catalogUuid,
          })
          stack.push(child.uuid)
        }
      }

      this.permissions = this.permissions.map(p => updates.get(p.uuid) ?? p)
      return updates.get(uuid)!
    },

    // Загрузка rolePermissions с пагинацией через ВСЕ страницы
    async fetchRolePermissions(roleUuid: string) {
      this.permissionsLoading = true
      try {
        const allRolePermissions: RolePermission[] = []
        let page = 1
        let hasMore = true

        while (hasMore) {
          const res = await Srv.query(
            'rolePermissions',
            ['uuid', 'name', 'attributes'],
            true,
            {
              filters: {
                role_uuid: {
                  operator: '=',
                  value: roleUuid
                }
              },
              page,
              perPage: 500
            }
          )

          const batch = (res.list || [])
            .map((rp: any) => {
              const attrs = attributesToObject(rp.attributes || [])

              // ВАЖНО: Гарантируем что states всегда массив
              let states = attrs.states || []
              if (!Array.isArray(states)) {
                states = [states]
              }

              return {
                uuid: rp.uuid,
                roleUuid: attrs.role_uuid || attrs.roleUuid,
                permissionUuid: attrs.permission_uuid || attrs.permissionUuid,
                states: states,
              }
            })
            .filter(rp => rp.states.length > 0) // Фильтруем только активные permissions (с непустыми states)

          allRolePermissions.push(...batch)

          hasMore = res.data?.hasMorePages ?? false
          page++
        }

        this.rolePermissions = allRolePermissions
        return this.rolePermissions
      } finally {
        this.permissionsLoading = false
      }
    },
    async createRolePermission(roleUuid: string, permissionUuid: string, states: string[]) {
      const res = await Srv.mutation(
        'rolePermissionCreate',
        ['uuid'],
        {
          attributes: objectToAttributes({
            role_uuid: roleUuid,
            permission_uuid: permissionUuid,
            states,
          })
        }
      )
      return res.data?.uuid as string | undefined
    },
    async updateRolePermission(uuid: string, states: string[]) {
      await Srv.mutation(
        'rolePermissionUpdate',
        ['uuid'],
        {
          uuid,
          attributes: objectToAttributes({states})
        }
      )
    },
    async saveRolePermissions(roleUuid: string, rolePermissions: RolePermission[]) {
      // Получаем текущие RolePermissions
      const current = await this.fetchRolePermissions(roleUuid)
      const currentMap = new Map(current.map(rp => [rp.permissionUuid, rp]))
      const newMap = new Map(rolePermissions.map(rp => [rp.permissionUuid, rp]))

      const promises: Promise<any>[] = []

      // Создаем новые или обновляем существующие
      for (const [permUuid, newRp] of newMap) {
        const existing = currentMap.get(permUuid)

        if (existing) {
          const existingStates = JSON.stringify([...existing.states].sort())
          const newStates = JSON.stringify([...newRp.states].sort())
          const statesChanged = existingStates !== newStates

          if (statesChanged) {
            promises.push(this.updateRolePermission(existing.uuid, newRp.states))
          }
        } else {
          if (newRp.states.length > 0) {
            promises.push(this.createRolePermission(roleUuid, permUuid, newRp.states))
          }
        }
      }

      // Обрабатываем удаленные permissions
      for (const [permUuid, existingRp] of currentMap) {
        if (!newMap.has(permUuid)) {
          promises.push(this.updateRolePermission(existingRp.uuid, []))
        }
      }

      await Promise.all(promises)
    },
  },
})
